import { FormError as AriaKitFormError } from '@ariakit/react'
import { ComponentProps, useCallback, useRef } from 'react'
import { useInView } from 'react-intersection-observer'
import { useUpdateEffect } from 'react-use'
import styled from 'styled-components'

import { ErrorMessage } from '@syconium/little-miss-figgy'

import { useIsValidFormField } from './useIsValidFormField'

export const FormError = (props: ComponentProps<typeof AriaKitFormError>) => {
  const isValid = useIsValidFormField(props)
  const ref = useRef<HTMLDivElement | null>(null)
  const { ref: callbackRef, inView } = useInView({
    threshold: 1,
    triggerOnce: false,
    fallbackInView: false,
  })
  // Must be stable. `callbackRef` sets state, so an inline ref (swapped by React every render)
  // re-renders on every commit and can hit React's max update depth.
  const mergedRef = useCallback(
    (newElement: HTMLDivElement | null) => {
      callbackRef(newElement)
      ref.current = newElement
    },
    [callbackRef]
  )

  useUpdateEffect(() => {
    if (!isValid && !inView) {
      const target = ref.current
        ?.closest('form')
        ?.querySelector('[role="alert"][data-error-visibility="visible"]')
      if (target === ref.current) {
        target?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
    }
  }, [isValid])

  return (
    <CollapsingErrorMessageWrapper inError={!isValid}>
      <AriaKitFormError
        data-error-visibility={isValid ? 'hidden' : 'visible'}
        ref={mergedRef}
        {...props}
      />
    </CollapsingErrorMessageWrapper>
  )
}

const CollapsingErrorMessageWrapper = styled(
  ({
    inError: _inError,
    ...props
  }: {
    inError: boolean
    children?: React.ReactNode
    className?: string
  }) => {
    return <ErrorMessage {...props} />
  }
)`
  ${o => (o.inError ? null : 'display: contents;')}
`
